/**
 * Access-token expiry inspection and the single-flight `/auth/refresh` exchange.
 *
 * This module is a **leaf** — it imports only axios, the API config and the
 * storage keys — so BOTH transports that carry a session token can share it:
 *
 * - `shared/api/axiosInstance` (every Orval hook), which refreshes proactively
 *   in its request interceptor, and
 * - `shared/auth/AuthContext`, whose bootstrap talks to `/auth/me` over raw
 *   `fetch` (`authApi`) and therefore never reaches an interceptor.
 *
 * Before the two shared this, the bootstrap had no expiry check at all: access
 * tokens live 15 minutes (`ACCESS_TOKEN_TTL`), so every hard navigation after a
 * short idle sent a known-dead token to `/auth/me` and logged a red 401 in the
 * console before refreshing and retrying. Sharing `refreshAccessTokens` also
 * collapses the bootstrap refresh and any query firing alongside it into ONE
 * `/auth/refresh` round trip instead of two racing exchanges.
 */
import axios from 'axios';

import { API_BASE_URL, API_ENDPOINTS } from '../api/config';
import { DEFAULT_REQUEST_TIMEOUT_MS } from '../api/requestTimeout';
import {
  SecureTokenStoreError,
  getRefreshToken,
  setRefreshToken,
} from './secureTokenStore';
import { AUTH_STORAGE_KEYS } from './storageKeys';

/** Treat a token as spent this many seconds before its `exp`. */
export const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

export interface TokenPair {
  access_token: string;
  refresh_token: string;
}

/**
 * A refresh exchange that did not yield a new token pair.
 *
 * `rejected` distinguishes "the server said this refresh token is no good"
 * (401/403 — the session is genuinely over) from a transient failure (offline,
 * 5xx, a CDN HTML page, a Keychain that would not open). Callers that must fail
 * closed treat both the same;
 * the bootstrap, which has to survive an offline cold start with a cached
 * session, only signs the user out on `rejected`.
 */
export class TokenRefreshError extends Error {
  constructor(
    message: string,
    public readonly rejected: boolean,
  ) {
    super(message);
    this.name = 'TokenRefreshError';
  }
}

/**
 * Decode a JWT payload without verifying it (base64 only — the server is the
 * only party that validates the signature).
 */
export function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Is this access token expired, about to expire, or unreadable?
 *
 * An undecodable token counts as expiring: sending it can only produce a 401,
 * so exchanging it first is strictly better than finding out from the server.
 */
export function isTokenExpiringSoon(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds + TOKEN_REFRESH_THRESHOLD_SECONDS;
}

/**
 * Is this access token the one minted for an active sysadmin emulation?
 *
 * Emulation swaps only the ACCESS token; the stored refresh token still belongs
 * to the sysadmin. Exchanging it would mint a fresh sysadmin access token and
 * silently drop the operator out of emulation, so an emulation token is never
 * refreshed — it simply expires (`EMULATION_TOKEN_TTL`, 1 hour).
 */
export function isEmulationAccessToken(token: string): boolean {
  return localStorage.getItem(AUTH_STORAGE_KEYS.emulationToken) === token;
}

/** In-flight exchange — concurrent callers join it instead of starting another. */
let refreshPromise: Promise<TokenPair> | null = null;

function isServerRejection(error: unknown): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 401 || status === 403;
}

/**
 * Exchange the stored refresh token for a fresh pair and persist it.
 *
 * Single-flight: while an exchange is in progress every other caller awaits the
 * same promise, so a cold load that refreshes from the auth bootstrap AND from
 * a query's request interceptor performs one rotation, not two.
 *
 * Throws `TokenRefreshError` on failure; the caller decides whether that ends
 * the session. Uses a bare `axios.post` (not the app instance) so it can never
 * recurse through the interceptors that call it — which also means it does NOT
 * inherit the instance's default deadline and must carry one itself. This is the
 * worst place in the app to hang: the request interceptor and the auth bootstrap
 * both await this before anything else goes out, so a stalled refresh freezes
 * every request in the app rather than just its own.
 */
export async function refreshAccessTokens(): Promise<TokenPair> {
  if (refreshPromise) {
    return refreshPromise;
  }

  // The token READ lives inside the memoised promise, not before it. Reading
  // the keystore is asynchronous, so hoisting it out would open an await point
  // ahead of single-flight registration: two concurrent callers would both pass
  // the guard above and each start an exchange of the same rotating refresh
  // token, the second of which the server has already blocklisted. Assigning
  // through a local — rather than reading `refreshPromise` back on the return —
  // keeps that correct even if the body ever stops awaiting.
  const exchange = (async (): Promise<TokenPair> => {
    try {
      const refreshToken = await getRefreshToken();
      if (!refreshToken) {
        throw new TokenRefreshError('No refresh token available', true);
      }

      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.auth.refresh}`,
        {},
        {
          timeout: DEFAULT_REQUEST_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      const tokens = response.data.data as TokenPair;

      // Persist only if the refresh token we just spent is still the device's.
      // A login, a logout or an emulation transition can land while the exchange
      // is on the wire — in this tab or in ANOTHER, since localStorage is shared
      // across the whole browser profile — and writing this pair afterwards
      // would OVERWRITE the incoming session's tokens with a departed session's,
      // or resurrect one that was just logged out. Same invariant the auth
      // bootstrap enforces one level up (`storedSessionIs` in AuthContext, and
      // .claude/rules/rules-cross-cutting-auth.md): re-read storage immediately
      // before touching it, never trust what the leg was reasoning about.
      //
      // The pair is still RETURNED either way. It is a valid credential for the
      // session it belongs to, and the caller — which knows whose leg it is —
      // decides what that is worth; skipping the write here only declines to
      // claim the device on its behalf.
      if ((await getRefreshToken()) !== refreshToken) {
        return tokens;
      }

      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
      await setRefreshToken(tokens.refresh_token);
      return tokens;
    } catch (error) {
      if (error instanceof TokenRefreshError) {
        throw error;
      }
      // A keystore that would not open is a DEVICE fault, not the server
      // revoking the session. Reporting it as `rejected` would sign the user out
      // over a locked Keychain — including on the offline cold start that the
      // auth bootstrap exists to survive.
      if (error instanceof SecureTokenStoreError) {
        throw new TokenRefreshError('Secure token store unavailable', false);
      }
      throw new TokenRefreshError('Token refresh failed', isServerRejection(error));
    } finally {
      refreshPromise = null;
    }
  })();
  refreshPromise = exchange;

  return exchange;
}
